import { Component, OnChanges, SimpleChanges, inject } from "@angular/core";
import { NgClass } from "@angular/common";
import { AbstractEffTestingStatusComponent } from "../abstract-eff-testing-status-component";
import { EffectivenessStatusType, SampleScore, TestStepScore } from "../../../shared/enums";
import { EffTableColumnConfig } from "../../common/eff-table/models/eff-table-column-config.model";
import { EffTableConfig } from "../../common/eff-table/models/eff-table-config.model";
import { EvidenceSampleTestStep } from "../models/evidence-sample-step.model";
import { PopupActionResult } from "../../common/popups/models/popup-action-result.model";
import { FinalConclusion } from "../components/eff-final-conclusion-box/models/final-conclusion.model";
import { provideTranslocoScope, TranslocoPipe, TranslocoService } from "@jsverse/transloco";
import { EffStepHeaderComponent } from "../../common/eff-step-header/eff-step-header.component";
import { EffDetailFinalConclusionComponent } from "../../eff-detail-final-conclusion/eff-detail-final-conclusion.component";
import { EffCheckboxComponent } from "../../common/eff-checkbox/eff-checkbox.component";
import { EffInlineTextEditorComponent } from "../../common/eff-inline-text-editor/eff-inline-text-editor.component";
import { DateFormatPipe } from "@app/shared/pipes/date-format.pipe";
import { DateTimeFormatPipe } from "@app/shared/pipes/date-time-format.pipe";
import { EffTableComponent, EffTableColumnDirective } from "../../common/eff-table/eff-table.component";
import { EffTextareaComponent } from "../../common/eff-textarea/eff-textarea.component";
import { DocumentsListComponent } from "../components/documents-list/documents-list.component";
import { EffIconComponent } from "../../common/eff-icon/eff-icon.component";
import { EffTestInstructionsInfoBoxComponent } from "../../info-boxes/eff-test-instructions-info-box/eff-test-instructions-info-box.component";
import { EffEvidenceInstructionsInfoBoxComponent } from "../../info-boxes/eff-evidence-instructions-info-box/eff-evidence-instructions-info-box.component";
import { EffSourceDocumentInfoBoxComponent } from "../../info-boxes/eff-source-document-info-box/eff-source-document-info-box.component";
import { EffControlInfoBoxComponent } from "../../info-boxes/eff-control-info-box/eff-control-info-box.component";

@Component({
    selector: "eff-testing-completed",
    templateUrl: "./eff-testing-completed.component.html",
    styleUrls: ["./eff-testing-completed.component.scss"],
    providers: [provideTranslocoScope("controls")],
    imports: [
        TranslocoPipe,
        NgClass,
        EffStepHeaderComponent,
        EffDetailFinalConclusionComponent,
        EffCheckboxComponent,
        EffInlineTextEditorComponent,
        DateFormatPipe,
        DateTimeFormatPipe,
        EffTableComponent,
        EffTableColumnDirective,
        EffTextareaComponent,
        DocumentsListComponent,
        EffIconComponent,
        EffTestInstructionsInfoBoxComponent,
        EffEvidenceInstructionsInfoBoxComponent,
        EffSourceDocumentInfoBoxComponent,
        EffControlInfoBoxComponent,
    ]
})
export class EffTestingCompletedComponent
    extends AbstractEffTestingStatusComponent
    implements OnChanges
{
    private _transloco = inject(TranslocoService);
    ngOnChanges(changes: SimpleChanges): void {
        if (changes.effectivenessModel) {
            this.isNonOccurrence =
                this.effectivenessModel.status === EffectivenessStatusType.nonOccurrenceCompleted;
        }
    }
    protected loadSamples: boolean = true;

    isNonOccurrence = false;

    tableConfig: EffTableConfig = {
        ExpandableRow: true,
        SeparateRows: true,
        RowClassFunc(item: EvidenceSampleTestStep) {
            if (item.isSample) {
                return "sample-row";
            }
            return "";
        },
    };

    displayedColumns: Array<EffTableColumnConfig> = [
        { PropertyName: "name", DisplayedNameKey: "controls.effGrid.columnName", IsTemplate: true, Width: "25%" },
        {
            PropertyName: "scoreTester",
            DisplayedNameKey: "controls.effGrid.columnScoreTester",
            IsTemplate: true,
            Width: "25%",
        },
        {
            PropertyName: "testerComment",
            DisplayedNameKey: "controls.effGrid.columnCommentTester",
            IsTemplate: true,
            Width: "25%",
        },
    ];

    override onLoad() {
        if (this.showReviewColumn) {
            this.displayedColumns.push({
                PropertyName: "reviewerComment",
                DisplayedNameKey: "controls.effGrid.columnReviewerComment",
                IsTemplate: true,
                Width: "25%",
            });
        }
    }

    hideReopenButton() {
        return !(this.isNonOccurrence
            ? this.stepPermissions.nonOccurrenceCompleteAllowed
            : this.stepPermissions.completeAllowed);
    }

    hasEvidence(item: EvidenceSampleTestStep) {
        return item.documents?.length > 0;
    }

    getScoreColor(item: EvidenceSampleTestStep) {
        if (!item.isSample) {
            switch (item.stepScore) {
                case TestStepScore.correct:
                    return "score-correct";
                case TestStepScore.inapplicable:
                    return "score-inapplicable";
                case TestStepScore.incorrect:
                    return "score-incorrect";
                case TestStepScore.missingEvidence:
                    return "score-missing-evidence";
                case TestStepScore.noScore:
                    return "no-score";
            }
        } else {
            switch (item.sampleScore) {
                case SampleScore.sufficient:
                    return "score-correct";
                case SampleScore.inapplicable:
                    return "score-inapplicable";
                case SampleScore.insufficient:
                    return "score-incorrect";
                case SampleScore.noScore:
                    return "no-score";
            }
        }
    }

    async reopenTestPlan() {
        if (!this.stepPermissions.isCompleteAllowed) {
            return;
        }

        if (this.isNonOccurrence) {
            var result = await this._dialogService.showConfirmationDialog(
                this._transloco.translate("controls.effTestingCompleted.reopenNonOccurrenceTitle"),
                this._transloco.translate("controls.effTestingCompleted.reopenNonOccurrenceMessage"),
            );

            if (result == PopupActionResult.OK) {
                this._workflowDs
                    .postWorkflowNonOccurrenceReopen(this.effectivenessModel.guid)
                    .subscribe(() => {
                        this.cerrixTab.refresh();
                    });
            }
        } else {
            this.giveFinalConclusion(
                this._transloco.translate("controls.effTestingCompleted.reopenTestPlanTitle"),
                this._transloco.translate("controls.effTestingCompleted.confirm"),
                null,
                ((finalConclusion: FinalConclusion) => {
                    this._workflowDs
                        .postWorkflowReopen(this.effectivenessModel.guid, {
                            comment: finalConclusion.conclusion,
                            scoreId: finalConclusion.score,
                        })
                        .subscribe(() => {
                            this.cerrixTab.refresh();
                        });
                }).bind(this),
                "600px",
                true,
            );
        }
    }
}
